import dayjs from "dayjs";
import * as Yup from "yup";
import { dateWithValidation } from "../../../helpers/constants/commonValidationSchemaUtils";
import { KeyValue } from "../../../helpers/types/common";
import {
  NCBPReportFormKeys,
  NCBPReportFormKeyTypes,
  outstandingAmountFormKeys,
  outstandingAmountValidations,
  SearchCoachReportFormKeys,
  SearchNotificationStatusReportFormKeys,
  SearchOutreachReportFormKeys,
  searchPendingBillingFormKeys,
  selectPendingBillingFormKeys,
} from "./models";

export const getReportSearchFormSchema = (requiredValidationMsg: string) =>
  Yup.object().shape({
    [selectPendingBillingFormKeys.categoryId]: Yup.string().required(
      requiredValidationMsg,
    ),
    [selectPendingBillingFormKeys.typeId]: Yup.string().required(
      requiredValidationMsg,
    ),
  });

export const getPendingBillingSearchFormSchema = (
  requiredValidationMsg: string,
) =>
  Yup.object().shape({
    [searchPendingBillingFormKeys.officeType]: Yup.string().required(
      requiredValidationMsg,
    ),
  });

export const NotificationStatusReportFormSchema = (
  requiredValidationMsg = "",
) =>
  Yup.object().shape({
    [SearchNotificationStatusReportFormKeys.marketOfDeliveryOffices]:
      Yup.array().min(1, requiredValidationMsg).required(requiredValidationMsg),
  });

export const OutreachReportFormSchema = (requiredValidationMsg = "") =>
  Yup.object().shape({
    [SearchOutreachReportFormKeys.marketOfDeliveryOffices]: Yup.array()
      .min(1, requiredValidationMsg)
      .required(requiredValidationMsg),
  });

export const getOutStandingAmountFormSchema = () =>
  Yup.object().shape({
    [outstandingAmountFormKeys.minAmount]: Yup.number().required(),
    [outstandingAmountFormKeys.maxAmount]: Yup.number().required(),
    [outstandingAmountFormKeys.currency]: Yup.string().default(""),
    [outstandingAmountFormKeys.minAmountWithFormat]: Yup.string()
      .default("")
      .test(outstandingAmountValidations.minMaxAmountRequired, "", function () {
        const {
          [outstandingAmountFormKeys.minAmount]: minAmount,
          [outstandingAmountFormKeys.maxAmount]: maxAmount,
          [outstandingAmountFormKeys.currency]: currency,
        } = this.parent;
        if (currency && minAmount === 0 && maxAmount === 0) {
          return false;
        }
        return true;
      })
      .test(
        outstandingAmountValidations.minAmountGreaterThanZero,
        "",
        function () {
          const {
            [outstandingAmountFormKeys.minAmount]: minAmount,
            [outstandingAmountFormKeys.currency]: currency,
          } = this.parent;
          if (currency && minAmount === 0) {
            return false;
          }
          return true;
        },
      )
      .test(
        outstandingAmountValidations.minAmountLessThanMaxAmount,
        "",
        function () {
          const {
            [outstandingAmountFormKeys.minAmount]: minAmount,
            [outstandingAmountFormKeys.maxAmount]: maxAmount,
            [outstandingAmountFormKeys.currency]: currency,
          } = this.parent;
          if (currency && maxAmount && minAmount >= maxAmount) {
            return false;
          }
          return true;
        },
      ),
    [outstandingAmountFormKeys.maxAmountWithFormat]: Yup.string()
      .default("")
      .test(outstandingAmountValidations.minMaxAmountRequired, "", function () {
        const {
          [outstandingAmountFormKeys.minAmount]: minAmount,
          [outstandingAmountFormKeys.maxAmount]: maxAmount,
          [outstandingAmountFormKeys.currency]: currency,
        } = this.parent;
        if (currency && minAmount === 0 && maxAmount === 0) {
          return false;
        }
        return true;
      })
      .test(
        outstandingAmountValidations.maxAmountGreaterThanMinAmount,
        "",
        function () {
          const {
            [outstandingAmountFormKeys.minAmount]: minAmount,
            [outstandingAmountFormKeys.maxAmount]: maxAmount,
            [outstandingAmountFormKeys.currency]: currency,
          } = this.parent;
          if (currency && maxAmount <= minAmount) {
            return false;
          }
          return true;
        },
      ),
  });

export const CoachReportFormSchema = (requiredValidationMsg = "") =>
  Yup.object().shape({
    [SearchCoachReportFormKeys.coaches]: Yup.array()
      .min(1, requiredValidationMsg)
      .required(requiredValidationMsg),
  });

export const NCBPReportSearchFormSchema = (
  commonValidationMessages: KeyValue,
  userCountryDateFormat: string,
): Yup.ObjectSchema<NCBPReportFormKeyTypes> =>
  Yup.object().shape({
    [NCBPReportFormKeys.dossierNumber]: Yup.string(),
    [NCBPReportFormKeys.actualClientId]: Yup.string(),
    [NCBPReportFormKeys.nonClientBillingPartyId]: Yup.string(),
    [NCBPReportFormKeys.invoiceStartDate]: dateWithValidation(
      true,
      commonValidationMessages,
      false,
      false,
      userCountryDateFormat,
    )
      .when([NCBPReportFormKeys.dossierNumber], {
        is: (dossierNumber: string) =>
          !dossierNumber || dossierNumber.trim() === "",
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema,
      })
      .test(
        "start-date-after-end-date",
        commonValidationMessages.invoiceStartDateValidationMessage,
        function (value) {
          const { invoiceEndDate } = this.parent;
          if (!value || !invoiceEndDate) return true;

          const startDate = dayjs(value, userCountryDateFormat);
          const endDate = dayjs(invoiceEndDate, userCountryDateFormat);

          return !startDate.isAfter(endDate);
        },
      ),
    [NCBPReportFormKeys.invoiceEndDate]: dateWithValidation(
      true,
      commonValidationMessages,
      false,
      false,
      userCountryDateFormat,
    ).when(
      [NCBPReportFormKeys.dossierNumber, NCBPReportFormKeys.invoiceStartDate],
      {
        is: (dossierNumber: string, invoiceStartDate: string) =>
          (!dossierNumber || dossierNumber.trim() === "") &&
          invoiceStartDate &&
          invoiceStartDate.trim() !== "",
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema,
      },
    ),
  });
